import React, { useState, useEffect } from 'react';
import { 
  getEmployees, createEmployee, updateEmployee, deleteEmployee, terminateEmployee,
  getEmployeeLeaves, createEmployeeLeave, updateEmployeeLeave, deleteEmployeeLeave, getEmployeeLeaveStats, bulkDeleteEmployees
} from '../services/api';
import Modal from '../components/Modal';
import ConfirmModal from '../components/Confirmmodal';
import Navbar from '../components/Navbar';
import { exportEmployeesToExcel } from '../utils/exportUtils';
import toast from 'react-hot-toast';
import '../styles/Pages.css';
import '../styles/Employees.css';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../utils/formatters';


const formatPhoneNumber = (value) => {
  if (!value) return '';
  let numbers = value.replace(/\D/g, '');
  if (numbers.startsWith('0')) {
    numbers = numbers.slice(1);
  }
  const limited = numbers.slice(0, 10);
  if (limited.length === 0) return '';
  if (limited.length <= 3) return `0(${limited}`;
  if (limited.length <= 6) return `0(${limited.slice(0, 3)}) ${limited.slice(3)}`;
  if (limited.length <= 8) return `0(${limited.slice(0, 3)}) ${limited.slice(3, 6)} ${limited.slice(6)}`;
  return `0(${limited.slice(0, 3)}) ${limited.slice(3, 6)} ${limited.slice(6, 8)} ${limited.slice(8)}`;
};


const normalizePhone = (phone) => {
  if (!phone) return '';
  return phone.replace(/\D/g, '');
};

function Employees() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLeaveModalOpen, setIsLeaveModalOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [editingLeave, setEditingLeave] = useState(null);  //YENİ: İzin düzenleme
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [searchTerm, setSearchTerm] = useState(() => {
    return localStorage.getItem('employeeSearch') || '';
  });
  const [activeFilter, setActiveFilter] = useState(() => {
    return localStorage.getItem('employeeActiveFilter') || 'all';
  });
  const [activeTab, setActiveTab] = useState('list');
  const { hasPermission } = useAuth();

  const [employeeLeaves, setEmployeeLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState(null);
  
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {},
    confirmText: 'Evet',
    loading: false
  });

  const [terminateDate, setTerminateDate] = useState('');
  const [showTerminateModal, setShowTerminateModal] = useState(false);
  const [terminatingEmployee, setTerminatingEmployee] = useState(null);

  const [formData, setFormData] = useState({
    full_name: '',
    tc_no: '',
    phone: '',
    email: '',
    address: '',
    position: '',
    department: '',
    hire_date: new Date().toISOString().split('T')[0],
    termination_date: '',
    is_active: true,
    is_insured: false,
    insurance_start_date: '',
    salary: '',
    payment_type: 'monthly',
    annual_leave_days: 14,
    notes: '',
    emergency_contact: '',
    emergency_phone: ''
  });

  const [leaveFormData, setLeaveFormData] = useState({
    employee_id: '',
    leave_type: 'yillik',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0],
    total_hours: 8,
    total_days: 1,
    description: ''
  });

  useEffect(() => {
    setLoading(true);
    fetchData(1).finally(() => setLoading(false));
  }, []);

  // Arama debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(1);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Filtre değişince
  useEffect(() => {
    fetchData(1);
  }, [activeFilter]);

  // Kalıcılık
  useEffect(() => {
    localStorage.setItem('employeeSearch', searchTerm);
  }, [searchTerm]);
  
  useEffect(() => {
    localStorage.setItem('employeeActiveFilter', activeFilter);
  }, [activeFilter]);

  const calculateBusinessDays = (startDateStr, endDateStr) => {
    const start = new Date(startDateStr + 'T00:00:00');
    const end = new Date(endDateStr + 'T00:00:00');
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 0;
    }
    
    if (end < start) {
      return 0;
    }
    
    let businessDays = 0;
    const current = new Date(start);
    
    while (current <= end) {
      const dayOfWeek = current.getDay();
      if (dayOfWeek !== 0) {
        businessDays++;
      }
      current.setDate(current.getDate() + 1);
    }
    
    return businessDays;
  };

  useEffect(() => {
    if (leaveFormData.start_date && leaveFormData.end_date) {
      const days = calculateBusinessDays(leaveFormData.start_date, leaveFormData.end_date);
      const hours = days * 8;
      setLeaveFormData(prev => ({
        ...prev, 
        total_hours: hours,
        total_days: days
      }));
    }
  }, [leaveFormData.start_date, leaveFormData.end_date]);

  useEffect(() => {
    const days = leaveFormData.total_hours / 8;
    setLeaveFormData(prev => ({
      ...prev,
      total_days: days
    }));
  }, [leaveFormData.total_hours]);

  const fetchData = async (page = 1, size) => {
    try {
      const currentSize = size || pageSize;
      const params = { page, limit: currentSize };
      if (searchTerm) params.search = searchTerm;
      if (activeFilter === 'active') params.is_active = true;
      else if (activeFilter === 'inactive') params.is_active = false;
      
      const employeesRes = await getEmployees(params);
      setEmployees(employeesRes.data.items || employeesRes.data);
      setTotalCount(employeesRes.data.total || 0);
      setTotalPages(employeesRes.data.pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeeLeaves = async (employeeId) => {
    try {
      const [leavesRes, statsRes] = await Promise.all([
        getEmployeeLeaves(employeeId),
        getEmployeeLeaveStats(employeeId).catch(() => ({ 
          data: { 
            total_days: 0, 
            by_type: {},
            annual_leave_days: 14,
            remaining_leave_days: 14
          } 
        }))
      ]);
      setEmployeeLeaves(leavesRes.data);
      setLeaveStats(statsRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('İzinler yüklenemedi');
    }
  };
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(() => {
    const saved = localStorage.getItem('employeePageSize');
    return saved ? Number(saved) : 50;
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectMode, setSelectMode] = useState(false);

  const handleOpenModal = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setFormData({
        full_name: employee.full_name || '',
        tc_no: employee.tc_no || '',
        phone: employee.phone || '',
        email: employee.email || '',
        address: employee.address || '',
        position: employee.position || '',
        department: employee.department || '',
        hire_date: employee.hire_date || new Date().toISOString().split('T')[0],
        termination_date: employee.termination_date || '',
        is_active: employee.is_active ?? true,
        is_insured: employee.is_insured ?? false,
        insurance_start_date: employee.insurance_start_date || '',
        salary: employee.salary || '',
        payment_type: employee.payment_type || 'monthly',
        annual_leave_days: employee.annual_leave_days || 14,
        notes: employee.notes || '',
        emergency_contact: employee.emergency_contact || '',
        emergency_phone: employee.emergency_phone || ''
      });
    } else {
      setEditingEmployee(null);
      setFormData({
        full_name: '',
        tc_no: '',
        phone: '',
        email: '',
        address: '',
        position: '',
        department: '',
        hire_date: new Date().toISOString().split('T')[0],
        termination_date: '',
        is_active: true,
        is_insured: false,
        insurance_start_date: '',
        salary: '',
        payment_type: 'monthly',
        annual_leave_days: 14,
        notes: '',
        emergency_contact: '',
        emergency_phone: ''
      });
    }
    setIsModalOpen(true);
  };
  
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingEmployee(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingEmployee ? 'Güncelleniyor...' : 'Oluşturuluyor...');
    
    const submitData = {
      full_name: formData.full_name,
      hire_date: formData.hire_date,
      is_active: formData.is_active,
      is_insured: formData.is_insured,
      payment_type: formData.payment_type || 'monthly',
      annual_leave_days: parseInt(formData.annual_leave_days) || 14
    };

    if (formData.tc_no && formData.tc_no.trim()) submitData.tc_no = formData.tc_no.trim();
    if (formData.phone && formData.phone.trim()) submitData.phone = formData.phone.trim();
    if (formData.email && formData.email.trim()) submitData.email = formData.email.trim();
    if (formData.address && formData.address.trim()) submitData.address = formData.address.trim();
    if (formData.position && formData.position.trim()) submitData.position = formData.position.trim();
    if (formData.department && formData.department.trim()) submitData.department = formData.department.trim();
    if (formData.notes && formData.notes.trim()) submitData.notes = formData.notes.trim();
    if (formData.emergency_contact && formData.emergency_contact.trim()) submitData.emergency_contact = formData.emergency_contact.trim();
    if (formData.emergency_phone && formData.emergency_phone.trim()) submitData.emergency_phone = formData.emergency_phone.trim();
    
    if (formData.termination_date) submitData.termination_date = formData.termination_date;
    if (formData.insurance_start_date) submitData.insurance_start_date = formData.insurance_start_date;
    
    if (formData.salary && !isNaN(parseFloat(formData.salary))) {
      submitData.salary = parseFloat(formData.salary);
    }
    
    try {
      if (editingEmployee) {
        await updateEmployee(editingEmployee.id, submitData);
        toast.success('Çalışan güncellendi!', { id: loadingToast });
      } else {
        await createEmployee(submitData);
        toast.success('Çalışan oluşturuldu!', { id: loadingToast });
      }
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız', { id: loadingToast });
    }
  };


  const handleOpenLeaveModal = (leave = null) => {
    if (leave) {
      // Düzenleme modu
      setEditingLeave(leave);
      setLeaveFormData({
        employee_id: selectedEmployee?.id || '',
        leave_type: leave.leave_type || 'yillik',
        start_date: leave.start_date || new Date().toISOString().split('T')[0],
        end_date: leave.end_date || new Date().toISOString().split('T')[0],
        total_hours: leave.total_hours || (leave.total_days * 8) || 8,
        total_days: leave.total_days || 1,
        description: leave.description || ''
      });
    } else {
      
      setEditingLeave(null);
      setLeaveFormData({
        employee_id: selectedEmployee?.id || '',
        leave_type: 'yillik',
        start_date: new Date().toISOString().split('T')[0],
        end_date: new Date().toISOString().split('T')[0],
        total_hours: 8,
        total_days: 1,
        description: ''
      });
    }
    setIsLeaveModalOpen(true);
  };

  const handleCloseLeaveModal = () => {
    setIsLeaveModalOpen(false);
    setEditingLeave(null);
  };

 
  const handleSubmitLeave = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading(editingLeave ? 'Güncelleniyor...' : 'Kaydediliyor...');
    
    try {
      const leaveData = {
        employee_id: selectedEmployee.id,
        leave_type: leaveFormData.leave_type,
        start_date: leaveFormData.start_date,
        end_date: leaveFormData.end_date,
        total_hours: parseFloat(leaveFormData.total_hours),
        total_days: parseFloat(leaveFormData.total_hours) / 8,
        description: leaveFormData.description
      };

      if (editingLeave) {
        // Güncelleme
        await updateEmployeeLeave(editingLeave.id, leaveData);
        toast.success('İzin güncellendi!', { id: loadingToast });
      } else {
        // Yeni ekleme
        await createEmployeeLeave(leaveData);
        toast.success('İzin kaydedildi!', { id: loadingToast });
      }
      
      handleCloseLeaveModal();
      fetchEmployeeLeaves(selectedEmployee.id);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'İşlem başarısız', { id: loadingToast });
    }
  };

  const handleViewEmployeeLeaves = (employee) => {
    setSelectedEmployee(employee);
    setActiveTab('leaves');
    fetchEmployeeLeaves(employee.id);
  };

  const handleDeleteLeaveClick = (leave) => {
    setConfirmModal({
      isOpen: true,
      title: 'İzin Kaydı Silme',
      message: `${getLeaveTypeLabel(leave.leave_type)} iznini (${leave.total_days} gün) silmek istediğinizden emin misiniz?`,
      type: 'danger',
      confirmText: 'Evet, Sil',
      loading: false,
      onConfirm: () => handleDeleteLeaveConfirm(leave.id)
    });
  };

  const handleDeleteLeaveConfirm = async (leaveId) => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      await deleteEmployeeLeave(leaveId);
      toast.success('İzin silindi!');
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchEmployeeLeaves(selectedEmployee.id);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Silme başarısız');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };
  const handleBulkDelete = () => {
    setConfirmModal({
      isOpen: true,
      title: 'Toplu Çalışan Silme',
      message: `${selectedIds.length} çalışanı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`,
      type: 'danger',
      confirmText: `Evet, ${selectedIds.length} Çalışanı Sil`,
      loading: false,
      onConfirm: handleBulkDeleteConfirm
    });
  };

  const handleBulkDeleteConfirm = async () => {
    setConfirmModal(prev => ({ ...prev, loading: true }));
    try {
      await bulkDeleteEmployees(selectedIds);
      toast.success(`${selectedIds.length} çalışan silindi`);
      setSelectedIds([]);
      setSelectMode(false);
      setConfirmModal(prev => ({ ...prev, isOpen: false }));
      fetchData(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme hatası');
      setConfirmModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleDeleteClick = (employee) => {
    setConfirmModal({
      isOpen: true,
      title: 'Çalışan Silme',
      message: `${employee.full_name} isimli çalışanı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz!`,
      type: 'danger',
      confirmText: 'Evet, Sil',
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
          await deleteEmployee(employee.id);
          toast.success('Çalışan silindi!');
          fetchData();
        } catch (error) {
          toast.error('Silme başarısız');
        } finally {
          setConfirmModal(prev => ({ ...prev, isOpen: false, loading: false }));
        }
      }
    });
  };

  const handleTerminateClick = (employee) => {
    setTerminatingEmployee(employee);
    setTerminateDate(new Date().toISOString().split('T')[0]);
    setShowTerminateModal(true);
  };

  const handleTerminateConfirm = async () => {
    if (!terminateDate) {
      toast.error('Lütfen işten çıkış tarihi seçin');
      return;
    }
    
    const loadingToast = toast.loading('İşlem yapılıyor...');
    try {
      await terminateEmployee(terminatingEmployee.id, terminateDate);
      toast.success('Çalışan işten çıkarıldı', { id: loadingToast });
      setShowTerminateModal(false);
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız', { id: loadingToast });
    }
  };

  const handleExport = async () => {
    const loadingToast = toast.loading('Veriler hazırlanıyor...');
    try {
      const params = { page: 1, limit: 1000 };
      if (searchTerm) params.search = searchTerm;
      if (activeFilter === 'active') params.is_active = true;
      else if (activeFilter === 'inactive') params.is_active = false;
      const res = await getEmployees(params);
      exportEmployeesToExcel(res.data.items || res.data);
      toast.success('Excel indirildi!', { id: loadingToast });
    } catch (error) {
      toast.error('İndirme hatası', { id: loadingToast });
    }
  };

  const getLeaveTypeLabel = (type) => {
    const labels = {
      'yillik': 'Yıllık İzin',
      'mazeret': 'Mazeret İzni',
      'hastalik': 'Hastalık İzni',
      'ucretsiz': 'Ücretsiz İzin'
    };
    return labels[type] || type;
  };

  const formatHours = (hours) => {
    const days = Math.floor(hours / 8);
    const remainingHours = hours % 8;
    
    if (days === 0) {
      return `${remainingHours} saat`;
    } else if (remainingHours === 0) {
      return `${days} gün`;
    } else {
      return `${days} gün ${remainingHours} saat`;
    }
  };

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <div className="page-container">
      <Navbar />

      <div className="page-content">
        {/* Tab Navigation */}
        <div className="employee-tabs">
          <button 
            className={activeTab === 'list' ? 'active' : ''} 
            onClick={() => {
              setActiveTab('list');
              setSelectedEmployee(null);
            }}
          >
            Çalışan Listesi
          </button>
          {selectedEmployee && (
            <button 
              className={activeTab === 'leaves' ? 'active' : ''} 
              onClick={() => setActiveTab('leaves')}
            >
              🏖️ {selectedEmployee.full_name} - İzinler
            </button>
          )}
        </div>

        {/* Çalışan Listesi Sekmesi */}
        {activeTab === 'list' && (
          <>
            <div className="page-header">
              <h2>Çalışanlar ({totalCount})</h2>
              <div className="header-actions">
                <button className="btn-export" onClick={handleExport}>
                  📥 Excel'e Aktar
                </button>
                {selectMode ? (
                  <>
                    {selectedIds.length > 0 && hasPermission('delete_employees') && (
                      <button onClick={handleBulkDelete}
                        style={{display:'flex', alignItems:'center', gap:'6px', padding:'10px 20px', background:'linear-gradient(135deg,#ff5252,#d32f2f)', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'550'}}
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                            <path d="M10 11v6M14 11v6"/>
                            <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                          </svg>
                          Seçilenleri Sil ({selectedIds.length})
                        </button>
                    )}
                    <button onClick={() => { setSelectMode(false); setSelectedIds([]); }}
                     style={{display:'flex', alignItems:'center', gap:'6px', padding:'10px 20px', background:'#f0f0f0', color:'#333', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'550'}}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <path d="M18 6L6 18M6 6l12 12"/>
                        </svg>
                        İptal
                      </button>
                  </>
                ) : (
                  hasPermission('delete_employees') && (
                    <button onClick={() => setSelectMode(true)}
                      style={{display:'flex', alignItems:'center', gap:'6px', padding:'10px 20px', background:'linear-gradient(135deg,#667eea,#764ba2)', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontWeight:'550'}}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <rect x="3" y="3" width="18" height="18" rx="3"/>
                        <path d="M7 12l4 4 6-7"/>
                      </svg>
                      Seç
                    </button>
                  )
                )}
                {hasPermission('create_employees') && (
                  <button className="btn-primary" onClick={() => handleOpenModal()}>
                    + Yeni Çalışan
                  </button>
                )}
              </div>
            </div>

            <div className="filters">
              <input
                type="text"
                placeholder="İsim, telefon veya pozisyon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="filter-select"
              >
                <option value="all">Tümü</option>
                <option value="active">Aktif</option>
                <option value="inactive">Pasif</option>
              </select>
            </div>

            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    {selectMode && (
                      <th style={{width: '40px'}}>
                        <input type="checkbox"
                          onChange={(e) => { if (e.target.checked) setSelectedIds(employees.map(emp => emp.id)); else setSelectedIds([]); }}
                          checked={selectedIds.length === employees.length && employees.length > 0} />
                      </th>
                    )}
                    <th>Ad Soyad</th>
                    <th>Telefon</th>
                    <th>Pozisyon</th>
                    <th>Departman</th>
                    <th>İşe Giriş</th>
                    <th>Yıllık İzin</th>
                    <th>Durum</th>
                    <th>Sigorta</th>
                    <th>Maaş</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(employee => (
                    <tr key={employee.id}>
                      {selectMode && (
                        <td>
                          <input type="checkbox"
                            checked={selectedIds.includes(employee.id)}
                            onChange={(e) => {
                              if (e.target.checked) setSelectedIds(prev => [...prev, employee.id]);
                              else setSelectedIds(prev => prev.filter(id => id !== employee.id));
                            }} />
                        </td>
                      )}
                      <td title={`#${employee.id}`}>{employee.full_name}</td>
                      <td>{employee.phone || '-'}</td>
                      <td>{employee.position || '-'}</td>
                      <td>{employee.department || '-'}</td>
                      <td>{formatDate(employee.hire_date)}</td>
                      <td>
                        <span className="badge badge-info">
                          {employee.annual_leave_days || 14} gün
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${employee.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {employee.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${employee.is_insured ? 'badge-success' : 'badge-warning'}`}>
                          {employee.is_insured ? 'Sigortalı' : 'Sigortasız'}
                        </span>
                      </td>
                      <td>{formatCurrency(employee.salary)}</td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="btn-view"
                            onClick={() => handleViewEmployeeLeaves(employee)}
                            title="İzinler"
                          >
                            🏖️
                          </button>
                          {hasPermission('edit_employees') && (
                            <button className="btn-edit" onClick={() => handleOpenModal(employee)} title="Düzenle">
                              ✎
                            </button>
                          )}
                          {hasPermission('edit_employees') && employee.is_active && (
                            <button className="btn-warning" onClick={() => handleTerminateClick(employee)} title="İşten Çıkar">
                              ⏸
                            </button>
                          )}
                          {hasPermission('delete_employees') && (
                            <button className="btn-delete" onClick={() => handleDeleteClick(employee)} title="Sil">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="3 6 5 6 21 6"/>
                                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                <path d="M10 11v6M14 11v6"/>
                                <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                              </svg>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalCount > 0 && (
            <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:'8px', padding:'20px', flexWrap:'wrap'}}>
              <select value={pageSize} onChange={(e) => { const s = Number(e.target.value); setPageSize(s); localStorage.setItem('employeePageSize', s); fetchData(1, s); }}
                style={{padding:'6px 10px', borderRadius:'6px', border:'1px solid #ddd', marginRight:'8px', cursor:'pointer', fontSize:'14px', background:'white'}}>
                <option value={50}>50 kayıt</option><option value={100}>100 kayıt</option><option value={250}>250 kayıt</option>
              </select>
              <button onClick={() => fetchData(1)} disabled={currentPage===1} style={{padding:'8px 12px', borderRadius:'6px', border:'1px solid #ddd', cursor:currentPage===1?'not-allowed':'pointer', background:currentPage===1?'#f5f5f5':'white'}}>«</button>
              <button onClick={() => fetchData(currentPage-1)} disabled={currentPage===1} style={{padding:'8px 12px', borderRadius:'6px', border:'1px solid #ddd', cursor:currentPage===1?'not-allowed':'pointer', background:currentPage===1?'#f5f5f5':'white'}}>‹</button>
              {totalPages > 1 && Array.from({length: totalPages}, (_, i) => i+1).filter(p => p===1 || p===totalPages || Math.abs(p-currentPage)<=2).reduce((acc, p, i, arr) => { if (i > 0 && p - arr[i-1] > 1) acc.push('...'); acc.push(p); return acc; }, []).map((p, i) => p==='...' ? <span key={`dots-${i}`} style={{padding:'8px 4px'}}>...</span> : <button key={p} onClick={() => fetchData(p)} style={{padding:'8px 14px', borderRadius:'6px', border:`1px solid ${currentPage===p?'#667eea':'#ddd'}`, background:currentPage===p?'linear-gradient(135deg,#667eea,#764ba2)':'white', color:currentPage===p?'white':'#333', cursor:'pointer', fontWeight:currentPage===p?'600':'normal'}}>{p}</button>)}
              <button onClick={() => fetchData(currentPage+1)} disabled={currentPage===totalPages} style={{padding:'8px 12px', borderRadius:'6px', border:'1px solid #ddd', cursor:currentPage===totalPages?'not-allowed':'pointer', background:currentPage===totalPages?'#f5f5f5':'white'}}>›</button>
              <button onClick={() => fetchData(totalPages)} disabled={currentPage===totalPages} style={{padding:'8px 12px', borderRadius:'6px', border:'1px solid #ddd', cursor:currentPage===totalPages?'not-allowed':'pointer', background:currentPage===totalPages?'#f5f5f5':'white'}}>»</button>
              <span style={{color:'#666', fontSize:'14px', marginLeft:'8px'}}>{((currentPage-1)*pageSize)+1}-{Math.min(currentPage*pageSize, totalCount)} / {totalCount} çalışan | Sayfa {currentPage} / {totalPages}</span>
            </div>
            )}
          </>
        )}

        {/* İzin Yönetimi Sekmesi */}
        {activeTab === 'leaves' && selectedEmployee && (
          <>
            <div className="page-header">
              <h2>{selectedEmployee.full_name} - İzin Kayıtları</h2>
              <div className="header-actions">
                {hasPermission('edit_employees') && (
                  <button className="btn-primary" onClick={() => handleOpenLeaveModal()}>
                    + Yeni İzin Ekle
                  </button>
                )}
              </div>
            </div>

            {/* Kalan İzin Kartı */}
            {leaveStats && (
              <div className="leave-summary-grid">
                <div className="leave-summary-card" style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white'
                }}>
                  <h4>📅 Yıllık İzin Hakkı</h4>
                  <div className="leave-stats">
                    <div className="stat">
                      <span className="value" style={{fontSize: '28px'}}>
                        {leaveStats.annual_leave_days || 14} gün
                      </span>
                      <span style={{fontSize: '14px', opacity: 0.9}}>
                        ({(leaveStats.annual_leave_days || 14) * 8} saat)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="leave-summary-card" style={{
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  color: 'white'
                }}>
                  <h4>✅ Kullanılan (Yıllık)</h4>
                  <div className="leave-stats">
                    <div className="stat">
                      <span className="value" style={{fontSize: '28px'}}>
                        {leaveStats.used_leave_days || 0} gün
                      </span>
                      <span style={{fontSize: '14px', opacity: 0.9}}>
                        ({leaveStats.used_leave_hours || 0} saat)
                      </span>
                    </div>
                  </div>
                </div>

                <div className="leave-summary-card" style={{
                  background: leaveStats.remaining_leave_days > 0 
                    ? 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
                    : 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                  color: 'white'
                }}>
                  <h4>⏳ Kalan İzin</h4>
                  <div className="leave-stats">
                    <div className="stat">
                      <span className="value" style={{fontSize: '28px'}}>
                        {leaveStats.remaining_leave_days || 0} gün
                      </span>
                      <span style={{fontSize: '14px', opacity: 0.9}}>
                        ({leaveStats.remaining_leave_hours || 0} saat)
                      </span>
                    </div>
                  </div>
                </div>

                {Object.entries(leaveStats.by_type || {}).map(([type, data]) => (
                  <div key={type} className="leave-summary-card">
                    <h4>{getLeaveTypeLabel(type)}</h4>
                    <div className="leave-stats">
                      <div className="stat">
                        <span className="value">{data.days || 0} gün</span>
                        <span style={{fontSize: '12px', color: '#666'}}>
                          ({data.hours || 0} saat)
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* İzinler Tablosu */}
            <div className="table-container" style={{marginTop: '20px'}}>
              <table>
                <thead>
                  <tr>
                    <th>İzin Türü</th>
                    <th>Başlangıç</th>
                    <th>Bitiş</th>
                    <th>Süre (Saat)</th>
                    <th>Süre (Gün)</th>
                    <th>Açıklama</th>
                    <th>Kaydeden</th>
                    <th>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {employeeLeaves.length === 0 ? (
                    <tr>
                      <td colSpan="9" style={{textAlign: 'center', padding: '40px'}}>
                        Henüz izin kaydı yok
                      </td>
                    </tr>
                  ) : (
                    employeeLeaves.map(leave => (
                      <tr key={leave.id}>
                        <td title={`#${leave.id}`}>
                          <span className={`badge ${
                            leave.leave_type === 'yillik' ? 'badge-success' :
                            leave.leave_type === 'mazeret' ? 'badge-info' :
                            leave.leave_type === 'hastalik' ? 'badge-warning' :
                            'badge-secondary'
                          }`}>
                            {getLeaveTypeLabel(leave.leave_type)}
                          </span>
                        </td>
                        <td>{formatDate(leave.start_date)}</td>
                        <td>{formatDate(leave.end_date)}</td>
                        <td><strong>{leave.total_hours || (leave.total_days * 8)} saat</strong></td>
                        <td>{leave.total_days} gün</td>
                        <td>{leave.description || '-'}</td>
                        <td>{leave.created_by || '-'}</td>
                        <td>
                          <div className="action-buttons">
                            {hasPermission('edit_employees') && (
                              <button 
                                className="btn-edit" 
                                onClick={() => handleOpenLeaveModal(leave)} 
                                title="Düzenle"
                              >
                                ✎
                              </button>
                            )}
                            {hasPermission('edit_employees') && (
                              <button 
                                className="btn-delete" 
                                onClick={() => handleDeleteLeaveClick(leave)} 
                                title="Sil"
                              >
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                                  <path d="M10 11v6M14 11v6"/>
                                  <path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
                                </svg>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Çalışan Ekleme/Düzenleme Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingEmployee ? 'Çalışan Düzenle' : 'Yeni Çalışan'}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <h3 style={{marginBottom: 15, color: '#666'}}>Kişisel Bilgiler</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Ad Soyad *</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({...formData, full_name: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>TC Kimlik No</label>
              <input
                type="text"
                value={formData.tc_no}
                onChange={(e) => setFormData({...formData, tc_no: e.target.value})}
                maxLength="11"
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Telefon</label>
              <input
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: formatPhoneNumber(e.target.value)})}
                placeholder="0(5XX) XXX XX XX"
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Adres</label>
            <textarea
              value={formData.address}
              onChange={(e) => setFormData({...formData, address: e.target.value})}
              rows="2"
            />
          </div>

          <h3 style={{marginTop: 25, marginBottom: 15, color: '#666'}}>İş Bilgileri</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Pozisyon</label>
              <input
                type="text"
                value={formData.position}
                onChange={(e) => setFormData({...formData, position: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Departman</label>
              <input
                type="text"
                value={formData.department}
                onChange={(e) => setFormData({...formData, department: e.target.value})}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>İşe Giriş Tarihi *</label>
              <input
                type="date"
                value={formData.hire_date}
                onChange={(e) => setFormData({...formData, hire_date: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>İşten Çıkış Tarihi</label>
              <input
                type="date"
                value={formData.termination_date}
                onChange={(e) => setFormData({...formData, termination_date: e.target.value})}
              />
            </div>
          </div>

          <div className="form-group" style={{marginTop: '15px'}}>
            <label style={{display: 'block', marginBottom: '10px', fontWeight: '600'}}>
              Yıllık İzin Hakkı
            </label>
            <div style={{display: 'flex', gap: '15px'}}>
              {[14, 20, 26].map(days => (
                <label key={days} style={{
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  padding: '9px 15px',
                  backgroundColor: formData.annual_leave_days === days ? '#667eea' : '#f0f0f0',
                  color: formData.annual_leave_days === days ? 'white' : '#333',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  border: formData.annual_leave_days === days ? '2px solid #667eea' : '2px solid #ddd',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}>
                  <input
                    type="radio"
                    name="annual_leave_days"
                    value={days}
                    checked={formData.annual_leave_days === days}
                    onChange={(e) => setFormData({...formData, annual_leave_days: parseInt(e.target.value)})}
                    style={{display: 'none'}}
                  />
                  {days} Gün
                </label>
              ))}
            </div>
          </div>

          <div className="form-row" style={{marginTop: '15px'}}>
            <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                style={{width: '18px', height: '18px', accentColor: '#667eea'}}
              />
              <span>Aktif Çalışan</span>
            </label>
            <label style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
              <input
                type="checkbox"
                checked={formData.is_insured}
                onChange={(e) => setFormData({...formData, is_insured: e.target.checked})}
                style={{width: '18px', height: '18px', accentColor: '#667eea'}}
              />
              <span>Sigortalı</span>
            </label>
          </div>

          {formData.is_insured && (
            <div className="form-group" style={{marginTop: '15px'}}>
              <label>Sigorta Başlangıç Tarihi</label>
              <input
                type="date"
                value={formData.insurance_start_date}
                onChange={(e) => setFormData({...formData, insurance_start_date: e.target.value})}
              />
            </div>
          )}

          <h3 style={{marginTop: 25, marginBottom: 15, color: '#666'}}>Maaş Bilgileri</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Maaş (₺)</label>
              <input
                type="number"
                step="0.01"
                value={formData.salary}
                onChange={(e) => setFormData({...formData, salary: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Ödeme Tipi</label>
              <select
                value={formData.payment_type}
                onChange={(e) => setFormData({...formData, payment_type: e.target.value})}
              >
                <option value="monthly">Aylık</option>
                <option value="daily">Günlük</option>
                <option value="hourly">Saatlik</option>
              </select>
            </div>
          </div>

          <h3 style={{marginTop: 25, marginBottom: 15, color: '#666'}}>Acil Durum İletişim</h3>

          <div className="form-row">
            <div className="form-group">
              <label>Acil Durum Kişisi</label>
              <input
                type="text"
                value={formData.emergency_contact}
                onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
              />
            </div>
            <div className="form-group">
              <label>Acil Durum Telefonu</label>
              <input
                type="text"
                value={formData.emergency_phone}
                onChange={(e) => setFormData({...formData, emergency_phone: formatPhoneNumber(e.target.value)})}
                placeholder="0(5XX) XXX XX XX"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Notlar</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              rows="3"
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              {editingEmployee ? 'Güncelle' : 'Oluştur'}
            </button>
          </div>
        </form>
      </Modal>

      {/* İzin Ekleme/Düzenleme Modal */}
      <Modal
        isOpen={isLeaveModalOpen}
        onClose={handleCloseLeaveModal}
        title={`${selectedEmployee?.full_name} - ${editingLeave ? 'İzin Düzenle' : 'İzin Ekle'}`}
        size="medium"
      >
        <form onSubmit={handleSubmitLeave}>
          <div className="form-group">
            <label>İzin Türü *</label>
            <select
              value={leaveFormData.leave_type}
              onChange={(e) => setLeaveFormData({...leaveFormData, leave_type: e.target.value})}
              required
            >
              <option value="yillik">Yıllık İzin</option>
              <option value="mazeret">Mazeret İzni</option>
              <option value="hastalik">Hastalık İzni</option>
              <option value="ucretsiz">Ücretsiz İzin</option>
            </select>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Başlangıç Tarihi *</label>
              <input
                type="date"
                value={leaveFormData.start_date}
                onChange={(e) => setLeaveFormData({...leaveFormData, start_date: e.target.value})}
                required
              />
            </div>
            <div className="form-group">
              <label>Bitiş Tarihi *</label>
              <input
                type="date"
                value={leaveFormData.end_date}
                onChange={(e) => setLeaveFormData({...leaveFormData, end_date: e.target.value})}
                min={leaveFormData.start_date}
                required
              />
            </div>
          </div>

          <div className="form-group" style={{
            padding: '20px',
            backgroundColor: '#e3f2fd',
            borderRadius: '10px',
            marginTop: '15px'
          }}>
            <label style={{fontWeight: '600', color: '#1565c0', marginBottom: '15px', display: 'block'}}>
              İzin Süresi (Saat)
            </label>
            
            <div style={{display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap'}}>
              {[
                {label: '0,5 saat', hours: 0.5},
                {label: '1 saat', hours: 1},
                {label: '2 saat', hours: 2},
                {label: '4 saat (Yarım gün)', hours: 4},
                {label: '8 saat (1 gün)', hours: 8},
                {label: '16 saat (2 gün)', hours: 16},
                {label: '24 saat (3 gün)', hours: 24},
                {label: '40 saat (5 gün)', hours: 40}
              ].map(opt => (
                <button
                  key={opt.hours}
                  type="button"
                  onClick={() => setLeaveFormData({...leaveFormData, total_hours: opt.hours})}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: leaveFormData.total_hours === opt.hours ? '#1976d2' : 'white',
                    color: leaveFormData.total_hours === opt.hours ? 'white' : '#333',
                    border: '1px solid #ddd',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
              <input
                type="number"
                step="0.5"
                min="0.5"
                value={leaveFormData.total_hours}
                onChange={(e) => setLeaveFormData({...leaveFormData, total_hours: e.target.value === '' ? '' : parseFloat(e.target.value) || 0})}
                style={{
                  width: '90px',
                  padding: '10px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  textAlign: 'center',
                  borderRadius: '8px',
                  border: '2px solid #1976d2'
                }}
                required
              />
              <span style={{fontSize: '16px', color: '#333'}}>saat</span>
              <span style={{
                padding: '8px 15px',
                backgroundColor: '#fff',
                borderRadius: '6px',
                fontWeight: '500'
              }}>
                = {(leaveFormData.total_hours / 8).toFixed(1)} gün
              </span>
            </div>
          </div>

          <div className="form-group" style={{marginTop: '15px'}}>
            <label>Açıklama</label>
            <textarea
              value={leaveFormData.description}
              onChange={(e) => setLeaveFormData({...leaveFormData, description: e.target.value})}
              rows="3"
              placeholder=""
            />
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={handleCloseLeaveModal}>
              İptal
            </button>
            <button type="submit" className="btn btn-primary">
              {editingLeave ? 'Güncelle' : 'Kaydet'}
            </button>
          </div>
        </form>
      </Modal>

      {/* İşten Çıkarma Modalı */}
      <Modal
        isOpen={showTerminateModal}
        onClose={() => setShowTerminateModal(false)}
        title="İşten Çıkarma"
        size="small"
      >
        <div className="terminate-modal-content">
          <div className="terminate-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <p className="terminate-message">
            <strong>{terminatingEmployee?.full_name}</strong> isimli çalışanı işten çıkarmak istediğinizden emin misiniz?
          </p>
          
          <div className="form-group">
            <label>İşten Çıkış Tarihi *</label>
            <input
              type="date"
              value={terminateDate}
              onChange={(e) => setTerminateDate(e.target.value)}
              required
            />
          </div>
          
          <div className="terminate-actions">
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowTerminateModal(false)}
            >
              İptal
            </button>
            <button 
              className="btn btn-warning" 
              onClick={handleTerminateConfirm}
            >
              İşten Çıkar
            </button>
          </div>
        </div>
      </Modal>

      {/* Onay Modalı */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        loading={confirmModal.loading}
      />
    </div>
  );
}

export default Employees;